import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import {
  getBuildingsByCompanyId,
  getBuildingWithRelations,
} from "../services/buildingService";
import { getFloorsByBuildingId } from "../services/floorService";
import { getCompanyNames } from "../services/companyService";
import { useFloorForm } from "../hooks/useFloorForm";
import FloorLayoutDesigner from "../components/ui/FloorLayoutDesigner";
import FloorDeskLayout from "../components/ui/FloorDeskLayout";

/**
 * Creating a floor runs in order, because each step needs the one before it:
 * the cells only exist once the floor does, and a desk can only be placed in a
 * cell the plan has declared a room. Each step is revealed by saving the
 * previous one, rather than sending the analyst back to the list to find the
 * floor again.
 */
const STEP = {
  details: "details",
  design: "design",
  desks: "desks",
};

/**
 * "1st", "2nd", "3rd", "21st" — used past the spelled-out names above, which
 * stop at the twentieth. A plain "th" produced "21th Floor".
 */
const ordinalSuffix = (n) => {
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return "th";
  return { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
};

const ORDINALS = [
  "Ground",
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
  "Eleventh",
  "Twelfth",
  "Thirteenth",
  "Fourteenth",
  "Fifteenth",
  "Sixteenth",
  "Seventeenth",
  "Eighteenth",
  "Nineteenth",
  "Twentieth",
];

const FloorCreatePage = () => {
  const { setActiveSection, setFloorId } = useAppContext();
  const { form, setForm, saving, save, backToList } = useFloorForm({
    mode: "create",
  });

  // Set once the floor exists; the design layout can only be drawn after that.
  const [createdFloorId, setCreatedFloorId] = useState(null);
  const [step, setStep] = useState(STEP.details);
  // Bumped when a desk is added or removed, so the plan above re-reads the
  // rooms' tables rather than showing counts that no longer exist.
  const [desksVersion, setDesksVersion] = useState(0);
  // And the other way: bumped whenever the plan is persisted, so the desk grid
  // re-reads the cells. Once the desk step is open the grid stays mounted, so
  // merging or repainting above it changes nothing on screen without this.
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [buildings, setBuildings] = useState([]);
  const [allFloors, setAllFloors] = useState([]);
  const [lastAuto, setLastAuto] = useState({
    building_id: null,
    code: "",
    name: "",
  });

  const buildingMap = useMemo(
    () =>
      Object.fromEntries(
        (buildings || []).map((b) => [Number(b.building_id), b]),
      ),
    [buildings],
  );

  useEffect(() => {
    setActiveSection("floor-create");
  }, [setActiveSection]);

  // Load companies for the company selector.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await getCompanyNames();
        if (mounted) setCompanies(Array.isArray(list) ? list : []);
      } catch (_) {
        if (mounted) setCompanies([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Load buildings for the selected company.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const companyIdNum = Number(selectedCompanyId);
      if (!Number.isFinite(companyIdNum) || companyIdNum <= 0) {
        if (mounted) setBuildings([]);
        return;
      }
      try {
        const list = await getBuildingsByCompanyId(companyIdNum);
        if (!mounted) return;
        const normalized = Array.isArray(list) ? list : [];
        setBuildings(normalized);
        const selectedId = Number(form.building_id);
        if (
          selectedId > 0 &&
          !normalized.some((b) => Number(b?.building_id) === selectedId)
        ) {
          setForm((prev) => ({ ...prev, building_id: "" }));
        }
      } catch (_) {
        if (mounted) setBuildings([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedCompanyId, form.building_id, setForm]);

  // Enrich the selected building with rows/columns (for the read-only display).
  useEffect(() => {
    let mounted = true;
    (async () => {
      const buildingIdNum = Number(form.building_id);
      if (!Number.isFinite(buildingIdNum) || buildingIdNum <= 0) return;
      try {
        const details = await getBuildingWithRelations(buildingIdNum);
        if (!mounted || !details) return;
        setBuildings((prev) => {
          const list = Array.isArray(prev) ? prev.slice() : [];
          const idx = list.findIndex(
            (b) => Number(b?.building_id) === buildingIdNum,
          );
          if (idx >= 0) list[idx] = { ...list[idx], ...details };
          else list.push(details);
          return list;
        });
        const companyId = Number(details.company_id);
        if (Number.isFinite(companyId) && companyId > 0) {
          setSelectedCompanyId(String(companyId));
        }
      } catch (_) {}
    })();
    return () => {
      mounted = false;
    };
  }, [form.building_id]);

  // Load existing floors of the selected building for auto-numbering.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const buildingIdNum = Number(form.building_id);
      if (!Number.isFinite(buildingIdNum) || buildingIdNum <= 0) {
        if (mounted) setAllFloors([]);
        return;
      }
      try {
        const list = await getFloorsByBuildingId(buildingIdNum);
        if (mounted) setAllFloors(Array.isArray(list) ? list : []);
      } catch (_) {
        if (mounted) setAllFloors([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [form.building_id]);

  // Auto-fill floor name and code on building selection.
  useEffect(() => {
    const bIdNum = Number(form.building_id);
    if (!Number.isFinite(bIdNum) || bIdNum <= 0) return;
    const b = buildingMap[bIdNum];
    if (!b) return;

    const re = /^Fl(\d+)/i;
    const floorsForBuilding = (allFloors || []).filter(
      (f) => Number(f.building_id) === bIdNum,
    );
    const lastIdx = floorsForBuilding.reduce((max, f) => {
      const m = re.exec(String(f.floor_code || f.floorCode || ""));
      if (!m) return max;
      const n = Number(m[1]);
      return Number.isFinite(n) && n > max ? n : max;
    }, -1);
    const nextIdx = lastIdx + 1; // 0 if none -> Ground

    const nameOrdinal =
      nextIdx === 0 ? "Ground" : ORDINALS[nextIdx] || `${nextIdx}${ordinalSuffix(nextIdx)}`;
    const autoName = `${nameOrdinal} Floor`;

    const bname = String(b.name || "").trim();
    const three = bname.slice(0, 3);
    const threeFormatted =
      three ? three[0].toUpperCase() + three.slice(1).toLowerCase() : "";
    const autoCode = `Fl${nextIdx}${threeFormatted}`;

    setForm((prev) => {
      const isPrevAutoCode =
        prev.floor_code === "" ||
        (lastAuto.building_id != null && prev.floor_code === lastAuto.code);
      const isPrevAutoName =
        prev.name === "" ||
        (lastAuto.building_id != null && prev.name === lastAuto.name);
      return {
        ...prev,
        floor_code: isPrevAutoCode ? autoCode : prev.floor_code,
        name: isPrevAutoName ? autoName : prev.name,
      };
    });
    setLastAuto((prev) => {
      if (
        Number(prev?.building_id) === bIdNum &&
        String(prev?.code || "") === autoCode &&
        String(prev?.name || "") === autoName
      ) {
        return prev;
      }
      return { building_id: bIdNum, code: autoCode, name: autoName };
    });
  }, [
    form.building_id,
    buildingMap,
    allFloors,
    lastAuto.building_id,
    lastAuto.code,
    lastAuto.name,
    setForm,
  ]);

  const selectedBuildingIdValue =
    form.building_id === null || form.building_id === undefined ?
      ""
    : String(form.building_id);

  /**
   * Create the floor, then stay here with its design layout open.
   *
   * The cells only exist once the floor does — the server generates one room
   * per grid cell and stamps the building's cores onto them — so the plan
   * cannot be drawn before this point. Navigating away and making the user
   * find the floor again to draw it would be the wrong order of work.
   */
  const handleCreate = async () => {
    const saved = await save({ navigateOnSuccess: false });
    if (!saved) return;
    const id = Number(saved?.floor_id);
    if (Number.isFinite(id) && id > 0) {
      setCreatedFloorId(id);
      setFloorId(id);
      setStep(STEP.design);
    } else {
      backToList();
    }
  };

  /**
   * Saving the plan opens the desk step, the same way creating the floor opened
   * the plan. A merge is a move within designing the plan, not the end of it,
   * so only a save advances.
   */
  const handleLayoutChange = (reason) => {
    // Every persisted change reaches the desk grid — a merge moves cells under
    // it just as much as a save does. Only what is ON SCREEN turns on the
    // reason.
    setLayoutVersion((n) => n + 1);
    if (reason === "save") setStep(STEP.desks);
  };

  return (
    <div className="page role-management">
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          paddingTop: 20,
        }}
      >
        <div className="left">
          <h1>Create Floor</h1>
          <div className="subtitle">
            Use Floor Code and Name. Rows/Columns are inherited from the
            selected building.
          </div>
        </div>
        <div className="right" style={{ flexShrink: 0, paddingRight: 12 }}>
          <button className="secondary-btn" onClick={backToList}>
            Back
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="role-card create-card" style={{ maxWidth: 1020 }}>
          <div className="field">
            <label>Company</label>
            <select
              value={selectedCompanyId}
              disabled={createdFloorId != null}
              onChange={(e) => {
                const value = String(e.target.value || "");
                setSelectedCompanyId(value);
                setForm((prev) => ({ ...prev, building_id: "" }));
              }}
            >
              <option value="">Select a company</option>
              {(companies || []).map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.name || `Company ${c.company_id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Building</label>
            <select
              value={selectedBuildingIdValue}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, building_id: e.target.value }))
              }
              disabled={!selectedCompanyId || createdFloorId != null}
            >
              <option value="">Select a building</option>
              {(buildings || []).map((b) => (
                <option key={b.building_id} value={String(b.building_id ?? "")}>
                  {b.name || `Building ${b.building_id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Floor Code</label>
            <input
              type="text"
              value={form.floor_code}
              readOnly={createdFloorId != null}
              disabled={createdFloorId != null}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, floor_code: e.target.value }))
              }
            />
          </div>

          <div className="field">
            <label>Floor Name</label>
            <input
              type="text"
              value={form.name}
              readOnly={createdFloorId != null}
              disabled={createdFloorId != null}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div className="field">
            <label>Rows (inherited)</label>
            <input
              type="number"
              value={buildingMap[Number(form.building_id)]?.rows ?? ""}
              readOnly
              disabled
            />
          </div>

          <div className="field">
            <label>Columns (inherited)</label>
            <input
              type="number"
              value={buildingMap[Number(form.building_id)]?.columns ?? ""}
              readOnly
              disabled
            />
          </div>

          {createdFloorId != null && (
            <>
              <div className="fdg-divider" />
              <FloorLayoutDesigner
                floorId={createdFloorId}
                mode="edit"
                onLayoutChange={handleLayoutChange}
                desksVersion={desksVersion}
              />
            </>
          )}

          {step === STEP.desks && createdFloorId != null && (
            <>
              <div className="fdg-divider" />
              <FloorDeskLayout
                floorId={createdFloorId}
                mode="edit"
                refreshToken={layoutVersion}
                onDesksChange={() => setDesksVersion((n) => n + 1)}
              />
            </>
          )}

          <div className="actions" style={{ marginTop: 16 }}>
            {step === STEP.details ?
              <>
                <button
                  className="primary-btn"
                  onClick={handleCreate}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Create"}
                </button>
                <button
                  className="secondary-btn"
                  onClick={backToList}
                  disabled={saving}
                >
                  Cancel
                </button>
              </>
            : step === STEP.design ?
              <>
                {/* Carries on to the last step, the same way Save layout does.
                    Everything so far is already persisted, so this only decides
                    what is on screen. Back, in the header, leaves entirely. */}
                <button
                  className="primary-btn"
                  onClick={() => setStep(STEP.desks)}
                >
                  Continue to desk layout
                </button>
                <span className="fdg-step-hint">
                  Saving the layout brings you here too.
                </span>
              </>
            : <button className="primary-btn" onClick={backToList}>
                Done
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorCreatePage;
